import { getFeatureFlag, isBoolean } from "@sinch/feature-flags";
import { MM_PAYMENT_TYPE } from "../api/mm/mm.types";
import { hasIntegration } from "../components/NavigationBar/utils";
import { INBOX_V2_VERSION, INBOX_V4_VERSION } from "../hooks/useInboxConfig";
import {
  ACCOUNT_SETTINGS_SSO,
  ACCOUNT_SETTINGS,
  ADDON_CARRIER_LOOKUP,
  ADDON_HLR_LOOKUP,
  ADDON_LINK_PREVIEWS,
  ADDON_MOBILE_LANDING_PAGES,
  ADDON_NUMBER_TYPE_LOOKUP,
  ADDON_SHORT_TRACKABLE_LINKS,
  ADDON,
  API_KEYS_BASIC,
  API_KEYS_HMAC,
  API_KEYS_LEGACY,
  BILLING_DETAILS,
  BROADCASTS,
  CONTACTS_GROUPS,
  CONTACTS,
  CONTACTS_CONTACT_FIELDS_MANAGE,
  CONTACTS_SEGMENTS,
  INBOX,
  MANAGEMENT_SUB_ACCOUNTS,
  MANAGEMENT_USERS,
  MESSAGING_SEND_SCHEDULED,
  PARTNERS_LIST_CUSTOMER_ACCOUNT,
  PRODUCTS_SELECTION,
  REPORTING_ABANDONED_CART,
  REPORTING_ACTIVITY_LOG,
  REPORTING_REPORT_DELIVERY_STATUS,
  REPORTING_REPORT_DETAILED_ALL,
  REPORTING_REPORT_DETAILED_MINE,
  REPORTING_REPORT_INSIGHT_ALL,
  REPORTING_REPORT_INSIGHT_MINE,
  REPORTING_REPORT_PARTNER_CUSTOMERS,
  REPORTING_REPORT_SCHEDULED,
  REPORTING_REPORT_SUB_ACCOUNTS,
  REPORTING_REPORT_USERS,
  TEMPLATES_RESTRICTED,
  TEMPLATES,
  WEB_WIDGET,
  WORKFLOWS_ABANDONED_CART,
  WORKFLOWS,
  ADDON_DEDICATED_NUMBER,
  ADDON_FAMILIAR_SENDER,
  ADDON_ALPHA_TAG,
  SOCIAL_PREPAID_USERS,
  SOCIAL_ADMIN_ACCESS,
  SOCIAL_USERS_ACCESS,
  SOCIAL_PROMPT_ADMIN,
  SOCIAL_PROMPT_USER,
  MESSAGING_LANDING_PAGE,
  MESSAGING_LANDING_PAGE_TEMPLATE_LIST,
} from "../portals/messagemedia/featureList";
import type { VendorConfigPlugin } from "../portals/messagemedia/vendorConfig/loadVendorConfigPlugin";
import type {
  IsRouteEnabledOptions,
  MmRouteConfig,
  MmRouteConfigMenu,
  RouteConfigBase,
} from "./types";
import { getNewUniqueRouteKey } from "./utils/navigationConfigUtils";

const isExtensionsV2Disabled = ({
  vendorPlugin,
}: {
  vendorPlugin?: VendorConfigPlugin;
}) => {
  const extensionsV2 = vendorPlugin?.getByKey("extensions-v2");

  const hasExtensionsV2 =
    (typeof extensionsV2 === "boolean" && extensionsV2) ||
    (typeof extensionsV2 === "object" && extensionsV2.production);
  return extensionsV2 !== undefined ? !hasExtensionsV2 : false;
};

const isOttMfeEnabled =
  (flag: string) =>
  ({ userRights }: IsRouteEnabledOptions): boolean => {
    if (getFeatureFlag(flag, isBoolean, false) && userRights) {
      if (userRights.includes(SOCIAL_PREPAID_USERS)) return false;
      return userRights.some((right) =>
        [
          SOCIAL_USERS_ACCESS,
          SOCIAL_ADMIN_ACCESS,
          SOCIAL_PROMPT_USER,
          SOCIAL_PROMPT_ADMIN,
        ].includes(right)
      );
    }
    return false;
  };

const MfeDashboard = {
  key: getNewUniqueRouteKey("Dashboard"),
  path: "/",
  isEnabled: () => getFeatureFlag("mfeDashboard", isBoolean, false),
  mfe: { name: "MfeDashboard", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-home",
    textKey: "menuItemDashboard",
    href: "/",
  },
} satisfies MmRouteConfigMenu;

const HubDashboard = {
  ...MfeDashboard,
  isEnabled: () => !getFeatureFlag("mfeDashboard", isBoolean, false),
  mfe: { name: "container", entry: "app" },
} satisfies MmRouteConfigMenu;

const Inbox = {
  key: getNewUniqueRouteKey("inbox"),
  path: "/messages/inbox-v2/*",
  mfe: { name: "container", entry: "app" },
  isEnabled: ({ inboxVersion }) =>
    !inboxVersion || inboxVersion === INBOX_V2_VERSION,
  allowedPermissions: [INBOX],
  menu: {
    position: "top",
    icon: "mm-messages",
    textKey: "menuItemInbox",
    href: "/messages/inbox-v2",
  },
} satisfies MmRouteConfigMenu;

const HTMXInbox = {
  key: getNewUniqueRouteKey("inbox"),
  path: "/messages/inbox-v4/*",
  mfe: { name: "HTMXInbox", entry: "MainEntry" },
  allowedPermissions: [INBOX],
  isEnabled: ({ inboxVersion }) => inboxVersion === INBOX_V4_VERSION,
  menu: {
    position: "top",
    icon: "mm-messages",
    textKey: "menuItemInbox",
    href: "/messages/inbox-v4",
  },
} satisfies MmRouteConfigMenu;

const Campaigns = {
  key: getNewUniqueRouteKey("campaigns"),
  path: "/messages/new-message",
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-campaign",
    textKey: "menuItemMessages",
    href: "/messages/new-message",
    subRouteSections: [
      {
        textKey: "menuItemMessagesSMS",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("new-message"),
            path: "/messages/new-message/*",
            textKey: "menuItemMessagesNewMessage",
            href: "/messages/new-message",
          },
          {
            key: getNewUniqueRouteKey("sent"),
            path: "/messages/sent-messages/*",
            textKey: "menuItemMessagesSent",
            href: "/messages/sent-messages",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
            ],
          },
          {
            key: getNewUniqueRouteKey("broadcast-reports"),
            path: "/messages/broadcasts/*",
            textKey: "menuItemMessagesBroadcastReport",
            href: "/messages/broadcasts",
            isEnabled: ({ vendorPlugin }) =>
              vendorPlugin?.isNZCarriers() || false,
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
            ],
          },
          {
            key: getNewUniqueRouteKey("broadcast-reports"),
            path: "/messages/broadcasts/*",
            textKey: "menuItemMessagesCampaignReport",
            href: "/messages/broadcasts",
            isEnabled: ({ vendorPlugin }) => !vendorPlugin?.isNZCarriers(),
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
            ],
          },
          {
            key: getNewUniqueRouteKey("scheduled-messages"),
            path: "/messages/scheduled-messages/*",
            textKey: "menuItemMessagesScheduled",
            href: "/messages/scheduled-messages",
            allowedPermissions: [MESSAGING_SEND_SCHEDULED, BROADCASTS],
          },
          {
            key: getNewUniqueRouteKey("drafts"),
            path: "/messages/drafts/*",
            textKey: "menuItemMessagesDrafts",
            href: "/messages/drafts",
            allowedPermissions: [BROADCASTS],
          },
          {
            key: getNewUniqueRouteKey("received-messages"),
            path: "/messages/received-messages/*",
            textKey: "menuItemMessagesReceived",
            href: "/messages/received-messages",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
              CONTACTS,
            ],
          },
        ],
      },
      {
        textKey: "menuItemMessagesSocial",
        subRoutes: [
          {
            isEnabled: isOttMfeEnabled("mmCampaignsHermesMfe"),
            mfe: { name: "CampaignsHermes", entry: "Container" },
            key: getNewUniqueRouteKey("campaigns-social"),
            path: "/v2/campaigns/*",
            textKey: "menuItemMessagesSocialAllCampaigns",
            href: "/v2/campaigns/all",
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const Automations = {
  key: getNewUniqueRouteKey("automation"),
  path: "/integrations/abandoned-carts/*",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [WORKFLOWS_ABANDONED_CART, WORKFLOWS],
  menu: {
    position: "top",
    icon: "mm-automation",
    textKey: "menuItemAutomation",
    href: "/integrations/abandoned-carts",
    subRouteSections: [
      {
        textKey: "menuItemAutomation",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("chatbot"),
            path: "/bots/*",
            mfe: { name: "BotBuilderV2", entry: "Container" },
            textKey: "menuItemAutomationBot",
            href: "/bots",
            isEnabled: () =>
              getFeatureFlag("mmBotBuilderMfe", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("abandoned-cart-automation"),
            path: "/integrations/abandoned-carts/*",
            textKey: "menuItemAutomationCartSms",
            href: "/integrations/abandoned-carts",
            allowedPermissions: [WORKFLOWS_ABANDONED_CART],
            isEnabled: ({
              integrations = [],
              integrationConnectionStates = [],
            }) =>
              integrations
                ? hasIntegration(
                    integrations,
                    integrationConnectionStates,
                    "bigcommerce"
                  ) ||
                  hasIntegration(
                    integrations,
                    integrationConnectionStates,
                    "shopify"
                  )
                : true,
          },
          {
            key: getNewUniqueRouteKey("account-automation"),
            path: "/automations/*",
            textKey: "menuItemAutomationMyAutomations",
            href: "/automations",
            allowedPermissions: [WORKFLOWS],
          },
          {
            key: getNewUniqueRouteKey("account-automation-templates"),
            path: "/automations/templates",
            textKey: "menuItemAutomationTemplates",
            href: "/automations/templates",
            allowedPermissions: [WORKFLOWS],
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const Templates = {
  key: getNewUniqueRouteKey("templates"),
  path: "/templates/messages/*",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [TEMPLATES, TEMPLATES_RESTRICTED],
  menu: {
    position: "top",
    icon: "mm-library",
    textKey: "menuItemTemplates",
    href: "/templates/messages",
    subRouteSections: [
      {
        textKey: "menuItemTemplates",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("templates-sms"),
            path: "/templates/messages/*",
            textKey: "menuItemTemplatesSMS",
            href: "/templates/messages",
          },
          {
            mfe: { name: "TemplateManager", entry: "Container" },
            isEnabled: ({ paymentType }) =>
              getFeatureFlag("mmTemplateManagerMfe", isBoolean, false) &&
              paymentType !== MM_PAYMENT_TYPE.Enum.PREPAID &&
              paymentType !== MM_PAYMENT_TYPE.Enum.PREPAID_MONEY,
            key: getNewUniqueRouteKey("templates-social"),
            path: "/settings/account/social-templates/*",
            textKey: "menuItemTemplatesSocialTemplates",
            href: "/settings/account/social-templates",
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const MobileLandingPages = {
  key: getNewUniqueRouteKey("mobile-landing-pages"),
  path: "/messages/landing-pages/*",
  mfe: { name: "container", entry: "app" },
  isEnabled: isExtensionsV2Disabled,
  allowedPermissions: [
    MESSAGING_LANDING_PAGE,
    MESSAGING_LANDING_PAGE_TEMPLATE_LIST,
  ],
  menu: {
    position: "top",
    icon: "mm-sms-plus",
    textKey: "menuItemMobileLandingPages",
    href: "/messages/landing-pages",
  },
} satisfies MmRouteConfigMenu;

const isContactV2Enabled = (mmFeatureFlags?: string[]) =>
  !!mmFeatureFlags?.includes("CONTACTS_V2_BACKEND");
const Contacts = {
  key: getNewUniqueRouteKey("contacts"),
  path: "/contacts/*",
  isEnabled: ({ userRights }) => !isContactV2Enabled(userRights),
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-contact",
    textKey: "menuItemContact",
    href: "/contacts/groups",
    subRouteSections: [
      {
        textKey: "menuItemContact",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("contact-groups"),
            path: "/contacts/groups",
            textKey: "menuItemContactGroups",
            href: "/contacts/groups",
            allowedPermissions: [CONTACTS_GROUPS],
          },
          {
            key: getNewUniqueRouteKey("all-contacts"),
            path: "/contacts",
            textKey: "menuItemContactAllContacts",
            href: "/contacts",
            allowedPermissions: [CONTACTS],
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const ContactsV2 = {
  key: getNewUniqueRouteKey("contacts-v2"),
  path: "/contacts-v2/contacts/*",
  isEnabled: ({ userRights }) => isContactV2Enabled(userRights),
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "top",
    icon: "mm-contact",
    textKey: "menuItemContactV2",
    href: "/contacts-v2/contacts",
    subRouteSections: [
      {
        textKey: "menuItemContactV2",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("all-contacts-v2"),
            path: "/contacts-v2/contacts/*",
            textKey: "menuItemContactV2AllContacts",
            href: "/contacts-v2/contacts",
            allowedPermissions: [CONTACTS],
          },
          {
            key: getNewUniqueRouteKey("contact-groups-v2"),
            path: "/contacts-v2/groups/*",
            textKey: "menuItemContactV2Lists",
            href: "/contacts-v2/groups",
            allowedPermissions: [CONTACTS],
          },
          {
            key: getNewUniqueRouteKey("contact-fields-v2"),
            path: "/contacts-v2/contact-fields/*",
            textKey: "menuItemContactV2ContactFields",
            href: "/contacts-v2/contact-fields",
            allowedPermissions: [CONTACTS_CONTACT_FIELDS_MANAGE],
          },
          {
            key: getNewUniqueRouteKey("all-segments-v2"),
            path: "/contacts-v2/segments/*",
            textKey: "menuItemContactV2Segments",
            href: "/contacts-v2/segments",
            allowedPermissions: [CONTACTS_SEGMENTS],
            isEnabled: () =>
              getFeatureFlag("contactsv2ShowSegments", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("history-v2"),
            path: "/contacts-v2/history/*",
            textKey: "menuItemContactV2History",
            href: "/contacts-v2/history",
            allowedPermissions: [CONTACTS],
            isEnabled: () =>
              getFeatureFlag("contactsV2MenuHistory", isBoolean, false),
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const Extensions = {
  key: getNewUniqueRouteKey("extensions"),
  path: "/integrations",
  mfe: { name: "container", entry: "app" },
  allowedPermissions: [ADDON],
  menu: {
    position: "bottom",
    icon: "mm-extension",
    textKey: "menuItemExtensions",
    href: "/integrations",
    subRouteSections: [
      {
        textKey: "menuItemExtensions",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("integrations"),
            path: "/integrations",
            textKey: "menuItemExtensionsIntegrations",
            href: "/integrations",
            isEnabled: ({ vendorPlugin }) =>
              vendorPlugin?.isBrandMM() ||
              vendorPlugin?.isBrandSimpleTexting() ||
              vendorPlugin?.isBrandMS() ||
              vendorPlugin?.isBrandSE() ||
              false,
          },
          {
            key: getNewUniqueRouteKey("extensions"),
            path: "/extensions",
            textKey: "menuItemExtensionsPowerUps",
            href: "/extensions",
            isEnabled: ({ userRights, vendorPlugin }) => {
              const channelV2Config = vendorPlugin?.getByKey(
                "numbers-and-channels-v2"
              );
              const hasChannelV2 =
                (typeof channelV2Config === "boolean" && channelV2Config) ||
                (typeof channelV2Config === "object" &&
                  channelV2Config.production);

              const ChannelV2PowerUps = [
                WEB_WIDGET,
                ADDON_LINK_PREVIEWS,
                ADDON_MOBILE_LANDING_PAGES,
                ADDON_SHORT_TRACKABLE_LINKS,
                ADDON_CARRIER_LOOKUP,
                ADDON_NUMBER_TYPE_LOOKUP,
                ADDON_HLR_LOOKUP,
              ];
              const ChannelPowerUps = [
                ADDON_DEDICATED_NUMBER,
                ADDON_FAMILIAR_SENDER,
                ADDON_ALPHA_TAG,
                ADDON_LINK_PREVIEWS,
                ADDON_MOBILE_LANDING_PAGES,
                ADDON_SHORT_TRACKABLE_LINKS,
                ADDON_CARRIER_LOOKUP,
                ADDON_NUMBER_TYPE_LOOKUP,
                ADDON_HLR_LOOKUP,
              ];
              const showPowerUps = hasChannelV2
                ? ChannelV2PowerUps.some((powerUp) =>
                    userRights?.includes(powerUp)
                  )
                : ChannelPowerUps.some((powerUp) =>
                    userRights?.includes(powerUp)
                  ) || false;
              return showPowerUps;
            },
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const Reports = {
  key: getNewUniqueRouteKey("reports"),
  path: "/reporting/*",
  mfe: { name: "container", entry: "app" },
  menu: {
    position: "bottom",
    icon: "mm-report",
    textKey: "menuItemReports",
    href: "/integrations/reporting/automation",
    subRouteSections: [
      {
        textKey: "menuItemReports",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("automation-reports"),
            path: "/integrations/reporting/automation/*",
            textKey: "menuItemReportsAutomationReports",
            href: "/integrations/reporting/automation",
            allowedPermissions: [REPORTING_ABANDONED_CART],
            isEnabled: ({
              integrations = [],
              integrationConnectionStates = [],
            }) =>
              integrations
                ? hasIntegration(
                    integrations,
                    integrationConnectionStates,
                    "bigcommerce"
                  ) ||
                  hasIntegration(
                    integrations,
                    integrationConnectionStates,
                    "shopify"
                  )
                : true,
          },
          {
            key: getNewUniqueRouteKey("detailed-reports"),
            path: "/reporting/detailed-reports/*",
            textKey: "menuItemReportsDetailedReports",
            href: "/reporting/detailed-reports",
            allowedPermissions: [
              REPORTING_REPORT_DETAILED_ALL,
              REPORTING_REPORT_DETAILED_MINE,
              REPORTING_REPORT_INSIGHT_ALL,
              REPORTING_REPORT_INSIGHT_MINE,
            ],
          },
          {
            key: getNewUniqueRouteKey("customer-reports"),
            path: "/reporting/my-customers/*",
            textKey: "menuItemReportsMyCustomers",
            href: "/reporting/my-customers",
            allowedPermissions: [REPORTING_REPORT_PARTNER_CUSTOMERS],
          },
          {
            key: getNewUniqueRouteKey("delivery-status"),
            path: "/reporting/delivery-status/*",
            textKey: "menuItemReportsDeliveryStatusReports",
            href: "/reporting/delivery-status",
            allowedPermissions: [REPORTING_REPORT_DELIVERY_STATUS],
          },
          {
            key: getNewUniqueRouteKey("scheduled-reports"),
            path: "/reporting/scheduled-reports/*",
            textKey: "menuItemReportsScheduledReports",
            href: "/reporting/scheduled-reports",
            allowedPermissions: [REPORTING_REPORT_SCHEDULED],
          },
          {
            key: getNewUniqueRouteKey("sub-accounts-report"),
            path: "/reporting/sub-account-usage/*",
            textKey: "menuItemReportsSubAccountUsage",
            href: "/reporting/sub-account-usage",
            allowedPermissions: [REPORTING_REPORT_SUB_ACCOUNTS],
          },
          {
            key: getNewUniqueRouteKey("user-usage-report"),
            path: "/reporting/user-usage-report/*",
            textKey: "menuItemReportsUserUsageReport",
            href: "/reporting/user-usage-report",
            allowedPermissions: [REPORTING_REPORT_USERS],
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

const Livechat = {
  key: getNewUniqueRouteKey("livechat"),
  path: "/social-channels/livechat/*",
  isEnabled: () => getFeatureFlag("mmLivechatMfe", isBoolean, false),
  mfe: { name: "LivechatSettings", entry: "Container" },
} satisfies RouteConfigBase;

export const settings = {
  key: getNewUniqueRouteKey("configuration"),
  path: "/account-settings/*",
  mfe: {
    name: getFeatureFlag("mfeaccount", isBoolean, false)
      ? "MfeAccount"
      : "container",
    entry: "app",
  },
  allowedPermissions: [
    ACCOUNT_SETTINGS,
    ACCOUNT_SETTINGS_SSO,
    API_KEYS_BASIC,
    API_KEYS_HMAC,
    API_KEYS_LEGACY,
    BILLING_DETAILS,
    REPORTING_ACTIVITY_LOG,
    PARTNERS_LIST_CUSTOMER_ACCOUNT,
    MANAGEMENT_SUB_ACCOUNTS,
    MANAGEMENT_USERS,
  ],
  menu: {
    position: "bottom",
    icon: "mm-settings",
    textKey: "menuItemMMSettings",
    href: "/account-settings",
    subRouteSections: [
      {
        textKey: "menuItemMMSettings",
        subRoutes: [
          {
            key: getNewUniqueRouteKey("account-settings"),
            path: "/account-settings/*",
            textKey: "menuItemMMSettingsAccount",
            href: "/account-settings",
            allowedPermissions: [
              ACCOUNT_SETTINGS,
              ACCOUNT_SETTINGS_SSO,
              REPORTING_ACTIVITY_LOG,
            ],
          },
          {
            key: getNewUniqueRouteKey("numbers-and-channels"),
            path: "/numbers/*",
            isEnabled: ({ vendorPlugin, userCountry }) =>
              vendorPlugin
                ? !vendorPlugin.isNZCarriers() &&
                  !(vendorPlugin.isBrandMM() && userCountry == "NZ")
                : false,
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsNumbersChannels",
            href: "/numbers",
          },
          {
            key: getNewUniqueRouteKey("messaging-settings"),
            path: "/messaging-settings/*",
            isEnabled: ({ vendorPlugin, userCountry }) =>
              vendorPlugin?.isNZCarriers() ||
              (vendorPlugin?.isBrandMM() && userCountry == "NZ") ||
              false,
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsMessagingSettings",
            href: "/messaging-settings",
          },
          {
            key: getNewUniqueRouteKey("plan-selection"),
            path: "/plan-selection/*",
            mfe: { name: "container", entry: "app" },
            textKey: "menuItemMMSettingsPlanSelection",
            href: "/plan-selection",
            allowedPermissions: [PRODUCTS_SELECTION],
          },
          {
            key: getNewUniqueRouteKey("social-channels"),
            path: "/social-channels/*",
            textKey: "menuItemMMSettingsSocialChannels",
            mfe: { name: "Channels", entry: "Container" },
            isEnabled: isOttMfeEnabled("showOneSaasSocialChannelsMfe"),
            href: "/social-channels",
          },
          {
            key: getNewUniqueRouteKey("billing"),
            path: "/billing/*",
            textKey: "menuItemMMSettingsBilling",
            mfe: { name: "container", entry: "app" },
            href: "/billing",
            allowedPermissions: [BILLING_DETAILS],
          },
          {
            key: getNewUniqueRouteKey("billing-v2"),
            path: "/billing-v2/*",
            textKey: "menuItemMMSettingsBillingV2",
            mfe: { name: "MfeBilling", entry: "App" },
            href: "/billing-v2",
            allowedPermissions: [BILLING_DETAILS],
            isEnabled: () => getFeatureFlag("mmInboxV2", isBoolean, false),
          },
          {
            key: getNewUniqueRouteKey("customer-accounts"),
            path: "/customer-accounts",
            textKey: "menuItemMMSettingsCustomerAccounts",
            href: "/customer-accounts",
            mfe: { name: "container", entry: "app" },
            allowedPermissions: [PARTNERS_LIST_CUSTOMER_ACCOUNT],
          },
          {
            key: getNewUniqueRouteKey("sub-accounts"),
            path: "/sub-accounts/*",
            textKey: "menuItemMMSettingsSubAccounts",
            mfe: { name: "container", entry: "app" },
            href: "/sub-accounts",
            isEnabled: () => !getFeatureFlag("mfeaccount", isBoolean, false),
            allowedPermissions: [MANAGEMENT_SUB_ACCOUNTS],
          },
          {
            key: getNewUniqueRouteKey("users"),
            path: "/users/*",
            textKey: "menuItemMMSettingsUsers",
            mfe: { name: "container", entry: "app" },
            href: "/users",
            allowedPermissions: [MANAGEMENT_USERS],
          },
          {
            key: getNewUniqueRouteKey("activity-log"),
            path: "/activity-log",
            textKey: "menuItemMMSettingsActivityLog",
            mfe: { name: "container", entry: "app" },
            href: "/activity-log",
            isEnabled: () => getFeatureFlag("mfeaccount", isBoolean, false),
            allowedPermissions: [REPORTING_ACTIVITY_LOG],
          },
          {
            key: getNewUniqueRouteKey("api-settings"),
            path: "/api-settings/*",
            textKey: "menuItemMMSettingsApiSettings",
            mfe: { name: "container", entry: "app" },
            href: "/api-settings",
            allowedPermissions: [
              API_KEYS_BASIC,
              API_KEYS_HMAC,
              API_KEYS_LEGACY,
            ],
          },
          {
            key: getNewUniqueRouteKey("chatbox-sms"),
            path: "/extensions/sms-chat-widget/*",
            textKey: "menuItemMMSettingsChatboxSms",
            mfe: { name: "container", entry: "app" },
            href: "/extensions/sms-chat-widget",
            isEnabled: isExtensionsV2Disabled,
            allowedPermissions: [WEB_WIDGET],
          },
        ],
      },
    ],
  },
} satisfies MmRouteConfigMenu;

export const loginHubPaths = [
  "/accept-invitation",
  "/bigcommerce/*",
  "/shopify/*",
  "/connect/*",
  "/dev-signup/*",
  "/forgot-password/*",
  "/invitation/*",
  "/login/*",
  "/opt-out-v2/*",
  "/opt-out/*",
  "/payment-success/*",
  "/pin-verification/*",
  "/profiling-questionnaire-role-and-company",
  "/profiling-questionnaire-intend",
  "/profiling-questionnaire-intend-use-case",
  "/profiling-questionnaire-channels",
  "/profiling-questionnaire-multiple-channels",
  "/profiling-questionnaire-multiple-channels",
  "/profiling-questionnaire-multiple-channels-other-tools",
  "/profiling-questionnaire-goals",
  "/register-new/*",
  "/register/*",
  "/registration/*",
  "/reset-password/*",
  "/set-password/*",
  "/shopify/*",
  "/sign-up/*",
  "/signup/*",
  "/sso-handler/*",
  "/verify/*",
  "/new-mm-registration/*",
  "/user-invitation",
];

/**
 * Paths loading the hub without the sideMenu
 */
export const nonMenuHubPaths = [
  ...loginHubPaths,
  "/support/*", //Support pages appear in the authed state of the app, but currently are not integrated in the new menu
  "/integrations/workato",
];

/**
 * Paths that should render the hub even if they're not part of the menu
 */
export const extraHubPaths = [
  ...nonMenuHubPaths,
  "/profile/*",
].map<MmRouteConfig>((path) => ({
  key: getNewUniqueRouteKey(path),
  path,
  mfe: { name: "container", entry: "app" },
})) satisfies MmRouteConfig[];

export const nonMenuRoutes = [...extraHubPaths, Livechat];
export const mmNavigationConfig = [
  HubDashboard,
  MfeDashboard,
  Inbox,
  HTMXInbox,
  Campaigns,
  Automations,
  Templates,
  MobileLandingPages,
  ContactsV2,
  Contacts,
  Extensions,
  Reports,
  settings,
];
